import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

const steps = [
  { number: 1, label: "Persönlich", title: "Über dich" },
  { number: 2, label: "Tüfteln", title: "Was du freiwillig baust" },
  { number: 3, label: "Rahmen", title: "Fast geschafft" },
];

const initialValues = {
  name: "",
  email: "",
  phone: "",
  university: "",
  course: "",
  project: "",
  project_url: "",
  debugging: "",
  office: false,
  german: false,
  privacy: false,
};

export function buildBrokenRequest(values, session) {
  const body = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    body.set(key, String(value));
  }
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Challenge-Session": session,
    },
    body,
  };
}

function Progress({ current }) {
  return (
    <div className="dev-progress" aria-label={`Schritt ${current} von 3`}>
      <div className="dev-progress-copy">
        <strong>Schritt {current} von 3</strong>
        <span>{steps[current - 1].label}</span>
      </div>
      <div className="dev-progress-bar" aria-hidden="true">
        <span style={{ width: `${(current / steps.length) * 100}%` }} />
      </div>
      <ol>
        {steps.map((step) => (
          <li key={step.number} className={step.number === current ? "current" : step.number < current ? "done" : ""}>
            <span>{step.number < current ? "✓" : step.number}</span>
            {step.label}
          </li>
        ))}
      </ol>
    </div>
  );
}

function TextField({ label, name, type = "text", value, onChange, required = true, help, autoComplete, error }) {
  return (
    <div className={`form-field ${error ? "has-error" : ""}`}>
      <label htmlFor={name}>
        {label} {required && <span aria-hidden="true">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {help && <small>{help}</small>}
      {error && <p className="field-error" id={`${name}-error`}>{error}</p>}
    </div>
  );
}

function LongField({ label, name, value, onChange, help, error, maxLength = 1800 }) {
  return (
    <div className={`form-field ${error ? "has-error" : ""}`}>
      <label htmlFor={name}>
        {label} <span aria-hidden="true">*</span>
      </label>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        required
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      <div className="field-meta">
        <small>{help}</small>
        <small>{value.length}/{maxLength}</small>
      </div>
      {error && <p className="field-error" id={`${name}-error`}>{error}</p>}
    </div>
  );
}

function CheckField({ label, name, checked, onChange, error }) {
  return (
    <div className={error ? "has-error" : ""}>
      <label className="checkbox-field dev-checkbox">
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={onChange}
          required
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${name}-error` : undefined}
        />
        <span>{label}</span>
      </label>
      {error && <p className="field-error" id={`${name}-error`}>{error}</p>}
    </div>
  );
}

function StepFields({ step, values, onChange, errors }) {
  if (step === 1) {
    return (
      <>
        <div className="form-row">
          <TextField label="Vor- und Nachname" name="name" value={values.name} onChange={onChange} autoComplete="name" error={errors.name} />
          <TextField label="E-Mail" name="email" type="email" value={values.email} onChange={onChange} autoComplete="email" error={errors.email} />
        </div>
        <TextField label="Telefon" name="phone" type="tel" value={values.phone} onChange={onChange} autoComplete="tel" error={errors.phone} />
        <div className="form-row">
          <TextField label="Hochschule" name="university" value={values.university} onChange={onChange} error={errors.university} />
          <TextField label="Studiengang und Fachsemester" name="course" value={values.course} onChange={onChange} error={errors.course} />
        </div>
      </>
    );
  }
  if (step === 2) {
    return (
      <>
        <LongField
          label="Dein eigenes Projekt"
          name="project"
          value={values.project}
          onChange={onChange}
          help="Was hast du gebaut, obwohl niemand dich darum gebeten hat? Was hat funktioniert, was ist kaputtgegangen?"
          error={errors.project}
        />
        <TextField
          label="Link, Repository oder Demo"
          name="project_url"
          type="url"
          value={values.project_url}
          onChange={onChange}
          required={false}
          help="Öffentlich oder mit kurzen Zugangshinweisen."
          error={errors.project_url}
        />
        <LongField
          label="Dein hartnäckigstes technisches Problem"
          name="debugging"
          value={values.debugging}
          onChange={onChange}
          help="Was dachtest du zuerst, wie hast du gesucht und was war tatsächlich die Ursache?"
          error={errors.debugging}
        />
      </>
    );
  }
  return (
    <div className="agreement-stack">
      <CheckField
        label="Regelmäßig vor Ort: Ich kann in unserem Hamburger Büro arbeiten."
        name="office"
        checked={values.office}
        onChange={onChange}
        error={errors.office}
      />
      <CheckField
        label="Deutsch: Ich spreche Deutsch auf muttersprachlichem beziehungsweise C2-Niveau."
        name="german"
        checked={values.german}
        onChange={onChange}
        error={errors.german}
      />
      <CheckField
        label="Datenschutz: Ich habe die Datenschutzhinweise zur Kenntnis genommen."
        name="privacy"
        checked={values.privacy}
        onChange={onChange}
        error={errors.privacy}
      />
    </div>
  );
}

export function validateStep(step, values) {
  const errors = {};
  const required = (name, message = "Bitte fülle dieses Feld aus.") => {
    if (!String(values[name] ?? "").trim()) errors[name] = message;
  };
  if (step === 1) {
    required("name");
    required("email");
    required("phone");
    required("university");
    required("course");
    if (!errors.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = "Bitte gib eine gültige E-Mail-Adresse ein.";
    }
  } else if (step === 2) {
    required("project");
    required("debugging");
    if (values.project_url) {
      try {
        new URL(values.project_url);
      } catch {
        errors.project_url = "Bitte gib eine vollständige URL ein, zum Beispiel https://…";
      }
    }
  } else {
    for (const name of ["office", "german", "privacy"]) {
      if (!values[name]) errors[name] = "Bitte bestätige diesen Punkt, um fortzufahren.";
    }
  }
  return errors;
}

export function ChallengeProblem({ problem }) {
  return (
    <div className="protocol-problem" role="alert" aria-live="polite">
      <p className="challenge-gentle-title">Die Übermittlung dieses Formulars ist absichtlich fehlerhaft.</p>
      <p>Eine erfolgreiche Übermittlung ist Teil deiner Bewerbung. Deine Angaben bleiben erhalten.</p>
      <div className="protocol-detail">
        <span>HTTP {problem.status || "—"}</span>
        <strong>{problem.title || "Übermittlung fehlgeschlagen"}</strong>
      </div>
      <p>{problem.detail || "Die Bewerbung wurde nicht angenommen."}</p>
      {problem.type && <code>{problem.type}</code>}
    </div>
  );
}

export function Application({ config, initialStep = 1 }) {
  const [values, setValues] = useState(initialValues);
  const [step, setStep] = useState(initialStep);
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState(null);
  const [errors, setErrors] = useState({});
  const title = useMemo(() => steps[step - 1].title, [step]);

  const onChange = (event) => {
    const { name, type, checked, value } = event.target;
    setValues((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const move = (direction) => {
    setProblem(null);
    setErrors({});
    setStep((current) => Math.max(1, Math.min(3, current + direction)));
  };

  const submit = async (event) => {
    event.preventDefault();
    const validation = validateStep(step, values);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      const firstInvalid = event.currentTarget.querySelector(`[name="${Object.keys(validation)[0]}"]`);
      firstInvalid?.focus();
      return;
    }
    if (step < 3) {
      move(1);
      return;
    }
    setSubmitting(true);
    setProblem(null);
    try {
      const response = await fetch(config.endpoint, buildBrokenRequest(values, config.session));
      const payload = await response.json().catch(() => ({
        title: "Unlesbare Serverantwort",
        detail: "Die Antwort konnte nicht als JSON gelesen werden.",
      }));
      if (!response.ok) {
        setProblem({ status: response.status, ...payload });
        return;
      }
      window.location.assign(payload.thank_you_url);
    } catch (error) {
      setProblem({
        status: 0,
        title: "Netzwerkfehler",
        detail: error instanceof Error ? error.message : "Die Anfrage ist fehlgeschlagen.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dev-form-layout">
      <Progress current={step} />
      <form className="dev-form-panel" onSubmit={submit} noValidate>
        <p className="eyebrow">Deine Bewerbung</p>
        <h3>{title}</h3>
        <StepFields step={step} values={values} onChange={onChange} errors={errors} />

        <div className="form-actions">
          {step > 1 && (
            <button className="button secondary" type="button" onClick={() => move(-1)}>
              Zurück
            </button>
          )}
          <button className="button primary" type="submit" disabled={submitting}>
            {step < 3 ? "Weiter" : submitting ? "Übermittlung läuft …" : "Bewerbung absenden"}
          </button>
        </div>
        {step === 3 && (
          <p className="challenge-submit-note">
            Die Übermittlung dieses Formulars ist absichtlich fehlerhaft.
          </p>
        )}
        {problem && <ChallengeProblem problem={problem} />}
      </form>
    </div>
  );
}

export function mount(root) {
  const config = {
    endpoint: root.dataset.endpoint,
    position: root.dataset.position,
    session: root.dataset.session,
  };
  createRoot(root).render(<Application config={config} />);
}

if (typeof document !== "undefined") {
  const root = document.getElementById("dev-application");
  if (root) {
    mount(root);
  }
}
